import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Upload, 
  Download, 
  Eye, 
  Trash2, 
  Search, 
  Filter,
  FileText,
  AlertCircle,
  CheckCircle,
  Building,
  UserCheck,
  Plus,
  X
} from 'lucide-react';
import { otherFileService, OtherFileUploadDTO } from '../services/otherFileService';
import { OtherFile } from '../services/api';

const OtherFileManagement: React.FC = () => {
  const [otherFiles, setOtherFiles] = useState<OtherFile[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<OtherFile[]>([]);
  const [selectedFileType, setSelectedFileType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadData, setUploadData] = useState<{
    title: string;
    fileType: string;
    file: File | null;
  }>({
    title: '',
    fileType: 'Investor Charter - Broking',
    file: null
  });
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const fileTypes = [
    'Investor Charter - Broking',
    'Investor Complaint',
    'Important DP Circular',
    'Important Exchange Circular'
  ];

  const fileTypeIcons = {
    'Investor Charter - Broking': FileText,
    'Investor Complaint': AlertCircle,
    'Important DP Circular': Building,
    'Important Exchange Circular': UserCheck
  };

  useEffect(() => {
    loadOtherFiles();
  }, []);

  useEffect(() => {
    filterFiles();
  }, [otherFiles, selectedFileType, searchTerm]);

  const loadOtherFiles = async () => {
    try {
      const files = await otherFileService.getAllOtherFiles();
      setOtherFiles(files);
    } catch (error) {
      // Error loading other files
      setError('Failed to load other files');
    }
  };

  const filterFiles = () => {
    let filtered = otherFiles;

    // Filter by file type
    if (selectedFileType !== 'all') {
      filtered = filtered.filter(file => file.strModuleType === selectedFileType);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(file =>
        file.strTitle.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredFiles(filtered);
  };

  const handleUpload = async () => {
    if (!uploadData.title.trim() || !uploadData.file) {
      setError('Please fill in all required fields');
      return;
    }

    setIsUploading(true);
    setError('');
    setSuccess('');

    try {
      // Check if there's already a file for this module type
      const existingFile = otherFiles.find(file => file.strModuleType === uploadData.fileType);
      
      const uploadDto: OtherFileUploadDTO = {
        title: uploadData.title.trim(),
        moduleType: uploadData.fileType,
        file: uploadData.file
      };

      await otherFileService.uploadOtherFile(uploadDto);
      
      // Show appropriate success message
      if (existingFile) {
        setSuccess(`File replaced successfully! Previous file "${existingFile.strTitle}" has been updated.`);
      } else {
        setSuccess('File uploaded successfully!');
      }
      
      // Reset form
      setUploadData({
        title: '',
        fileType: uploadData.fileType,
        file: null
      });
      setIsUploadModalOpen(false);
      
      // Reload files
      await loadOtherFiles();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      // Error uploading file
      setError(error instanceof Error ? error.message : 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (id: string) => {
    try {
      await otherFileService.downloadOtherFile(id);
    } catch (error) {
      // Error downloading file
      setError('Failed to download file');
    }
  };

  const handleShow = async (id: string) => {
    try {
      await otherFileService.showOtherFile(id);
    } catch (error) {
      // Error showing file
      setError('Failed to show file');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await otherFileService.deleteOtherFile(id);
      await loadOtherFiles();
    } catch (error) {
      // Error deleting file
      setError('Failed to delete file');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Other Files Management</h2>
            <p className="text-gray-600">Manage and upload important documents and forms</p>
          </div>
        </div>
        <Button 
          onClick={() => setIsUploadModalOpen(true)}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200"
        >
          <Plus className="h-4 w-4 mr-2" />
          Upload File
        </Button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-200 rounded-xl shadow-lg">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <span className="text-green-800 font-medium">{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-xl shadow-lg">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <span className="text-red-800 font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="border-2 border-blue-100 shadow-lg">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Module Type Filter */}
            <div className="flex-1">
              <Label htmlFor="fileType" className="text-sm font-medium text-gray-700 mb-2 block">
                Module Type
              </Label>
              <select
                id="fileType"
                value={selectedFileType}
                onChange={(e) => setSelectedFileType(e.target.value)}
                className="block w-full px-4 py-3 border-2 border-gray-200 focus:border-blue-400 rounded-xl shadow-sm focus:outline-none bg-white text-gray-900 transition-all duration-200"
              >
                <option value="all">All Module Types</option>
                {fileTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div className="flex-1">
              <Label htmlFor="search" className="text-sm font-medium text-gray-700 mb-2 block">
                Search Files
              </Label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="search"
                  type="text"
                  placeholder="Search by title or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-12 border-2 border-gray-200 focus:border-blue-400 rounded-xl transition-all duration-200"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Files List */}
      <Card className="border-2 border-blue-100 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold text-gray-900">Files ({filteredFiles.length})</CardTitle>
              <CardDescription className="text-gray-600">
                {selectedFileType === 'all' ? 'All module types' : selectedFileType}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {filteredFiles.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Files Found</h3>
              <p className="text-gray-600">
                {selectedFileType === 'all' 
                  ? 'Get started by uploading a new file.'
                  : `No files found for ${selectedFileType}.`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFiles.map((file) => {
                const IconComponent = fileTypeIcons[file.strFileType as keyof typeof fileTypeIcons] || FileText;
                return (
                  <div
                    key={file.strGUID}
                    className="group hover:shadow-lg transition-all duration-300 border-2 border-gray-100 hover:border-blue-200 rounded-xl p-6 bg-white"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                          <IconComponent className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-gray-900 group-hover:text-blue-600 transition-colors">
                            {file.strTitle}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2">
                            {file.strModuleType}
                          </p>
                          <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                              {formatDate(file.dtCreatedDate)}
                            </span>
                            <span className="text-sm text-gray-500 bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                              {file.strFileType?.toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-500">
                              {formatFileSize(file.filesize)}
                            </span>
                            <span className="text-sm text-gray-500 bg-green-100 text-green-700 px-3 py-1 rounded-full">
                              {file.downloadCount || 0} downloads
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleShow(file.strGUID)}
                          className="border-2 border-blue-200 hover:border-blue-400 transition-all duration-200"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Show
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(file.strGUID)}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(file.strGUID)}
                          className="border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 transition-all duration-200"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-lg border-2 border-blue-100 shadow-2xl">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Upload className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Upload New File</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsUploadModalOpen(false)}
                className="hover:bg-gray-100 rounded-full p-2"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-6">
              <div>
                <Label htmlFor="uploadTitle" className="text-sm font-medium text-gray-700 mb-2 block">
                  Title *
                </Label>
                <Input
                  id="uploadTitle"
                  type="text"
                  value={uploadData.title}
                  onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                  placeholder="Enter file title"
                  className="h-12 border-2 border-gray-200 focus:border-blue-400 rounded-xl transition-all duration-200"
                />
              </div>

              <div>
                <Label htmlFor="uploadFileType" className="text-sm font-medium text-gray-700 mb-2 block">
                  Module Type *
                </Label>
                <select
                  id="uploadFileType"
                  value={uploadData.fileType}
                  onChange={(e) => setUploadData({ ...uploadData, fileType: e.target.value })}
                  className="block w-full px-4 py-3 border-2 border-gray-200 focus:border-blue-400 rounded-xl shadow-sm focus:outline-none bg-white text-gray-900 transition-all duration-200"
                >
                  {fileTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="uploadFile" className="text-sm font-medium text-gray-700 mb-2 block">
                  File *
                </Label>
                
                {/* File Upload Area */}
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition-all duration-200">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <Input
                    id="uploadFile"
                    type="file"
                    onChange={(e) => setUploadData({ ...uploadData, file: e.target.files?.[0] || null })}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                    className="hidden"
                  />
                  <label htmlFor="uploadFile" className="cursor-pointer">
                    <span className="text-sm font-medium text-blue-600 hover:text-blue-700">
                      Choose a file
                    </span>
                    <span className="text-xs text-gray-500 block mt-1">
                      or drag and drop
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Supported formats: PDF, DOC, DOCX, XLS, XLSX, TXT
                  </p>
                </div>

                {/* Selected File Display */}
                {uploadData.file && (
                  <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <FileText className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-green-900 text-sm">
                          {uploadData.file.name}
                        </h4>
                        <p className="text-xs text-green-700">
                          {(uploadData.file.size / 1024 / 1024).toFixed(2)} MB • {uploadData.file.type || 'Unknown type'}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUploadData({ ...uploadData, file: null })}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-8">
              <Button
                variant="outline"
                onClick={() => setIsUploadModalOpen(false)}
                className="border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0 shadow-lg transition-all duration-200 px-6"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherFileManagement; 